import { useEffect, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';
import type { Tenant } from '../types';

export type TenantWebSocketEvent = Partial<Tenant> & {
  id: string;
};

interface UseTenantWebSocketOptions {
  enabled?: boolean;
  onEvent?: (event: TenantWebSocketEvent) => void;
}

/**
 * Subscribe to tenant stream events for a single tenant id.
 */
export function useTenantWebSocket(
  tenantId?: string | null,
  options: UseTenantWebSocketOptions = {},
) {
  const { state, addObserver, removeObserver } = useWebSocket();
  const { onEvent, enabled = true } = options;

  const handleEvent = useCallback(
    (data: TenantWebSocketEvent, action: string) => {
      if (action !== 'update' && action !== 'create') {
        return;
      }
      const normalized = transformKeys<TenantWebSocketEvent>(data);
      if (tenantId && normalized.id !== tenantId) {
        return;
      }
      onEvent?.(normalized);
    },
    [tenantId, onEvent],
  );

  useEffect(() => {
    if (!enabled || !tenantId || !state.isConnected || !onEvent) {
      return;
    }

    const observerId = addObserver(
      'tenant',
      { data: { target: tenantId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
      },
    );

    return () => {
      removeObserver(observerId);
    };
  }, [enabled, tenantId, state.isConnected, addObserver, removeObserver, handleEvent, onEvent]);

  return {
    isConnected: Boolean(tenantId) && state.isConnected,
  };
}

export default useTenantWebSocket;
